import { useState } from 'react';

import { InfoHint } from '@/components/info-hint';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import type { InvoiceRow } from '../types';
import { AdjustmentNoteDialog } from './adjustment-note-dialog';

type NoteKind = 'Credit' | 'Debit';

export interface CreateNextDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly blOrderName?: string | null;
  readonly onCreated?: (() => void) | undefined;
}

function KindToggle({ kind, onKind }: Readonly<{ kind: NoteKind; onKind: (k: NoteKind) => void }>) {
  return (
    <div className="flex items-center gap-2" role="group" aria-label="Note kind">
      {(['Credit', 'Debit'] as const).map((option) => (
        <Button
          key={option}
          type="button"
          size="sm"
          variant={kind === option ? 'default' : 'outline'}
          data-testid={`create-next-kind-${option.toLowerCase()}`}
          onClick={() => {
            onKind(option);
          }}
        >
          {option} Note
        </Button>
      ))}
      <InfoHint
        label="credit / debit note amounts"
        ariaLabel="What credit and debit note amounts mean"
        hint={
          'A credit note reduces the amount owed on this side and shows as a negative signed ' +
          'amount; a debit note increases it and shows as a positive signed amount. The chain ' +
          'nets to the corrected total.'
        }
      />
    </div>
  );
}

function CreateNextHeader({
  kind,
  onKind,
}: Readonly<{ kind: NoteKind; onKind: (k: NoteKind) => void }>) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Create next document</DialogTitle>
        <DialogDescription>
          Create a Credit or Debit Note from this invoice. The document it adjusts is fixed to this
          invoice — you don&apos;t pick a parent.
        </DialogDescription>
      </DialogHeader>
      <KindToggle kind={kind} onKind={onKind} />
    </>
  );
}

function noop(): void {
  /* no-op */
}

// The per-invoice follow-on path: the same Credit/Debit Note form the creation
// buttons open (including declared price/weight changes), with the kind chosen
// up top and the parent fixed to the invoice the action was launched from.
export function CreateNextDialog(props: Readonly<CreateNextDialogProps>) {
  const [kind, setKind] = useState<NoteKind>('Credit');
  return (
    <AdjustmentNoteDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      side={props.side}
      noteType={kind}
      orderId={props.orderId}
      blOrderId={props.blOrderId}
      {...(props.blOrderName !== undefined ? { blOrderName: props.blOrderName } : {})}
      existingInvoice={null}
      prefillParentId={props.invoice.id}
      parentLocked
      headerSlot={<CreateNextHeader kind={kind} onKind={setKind} />}
      onSaved={props.onCreated ?? noop}
    />
  );
}
